export interface OnlinePlayer {
  name: string;
  firstSeen: string;
  lastSeen: string;
  currentlyOnline: boolean;
  currentSessionStart: string | null;
}

export interface DomainSnapshot {
  domain: string;
  players: OnlinePlayer[];
  lastPolledAt: string;
  pollingBotCount: number;
}

export interface DomainSummary {
  domain: string;
  onlineCount: number;
  totalKnownCount: number;
  lastPolledAt: string;
  pollingBotCount: number;
}

export interface OnlineSession {
  onlineAt: string;
  offlineAt: string | null;
}

export interface PollWindow {
  startedAt: string;
  endedAt: string | null;
}

export interface PlayerHistory {
  domain: string;
  playerName: string;
  firstSeen: string | null;
  lastSeen: string | null;
  currentlyOnline: boolean;
  sessions: OnlineSession[];
  pollWindows: PollWindow[];
}

export function subscribeDomainStream(
  domain: string,
  onSnapshot: (snapshot: DomainSnapshot) => void,
  onConnection?: (connected: boolean) => void
): () => void {
  let es: EventSource | null = null;
  let reconnectTimer: number | null = null;
  let closed = false;

  const open = () => {
    if (closed) return;
    es = new EventSource(`/api/onlineplayers/${encodeURIComponent(domain)}/stream`);

    es.addEventListener("open", () => onConnection?.(true));

    es.addEventListener("message", (e: MessageEvent) => {
      try {
        const snap: DomainSnapshot = JSON.parse(e.data);
        onSnapshot(snap);
      } catch { /* ignore malformed */ }
    });

    es.addEventListener("error", () => {
      onConnection?.(false);
      if (es?.readyState === EventSource.CLOSED && reconnectTimer == null) {
        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;
          open();
        }, 2000);
      }
    });
  };

  open();

  return () => {
    closed = true;
    if (reconnectTimer != null) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    es?.close();
    es = null;
  };
}
